// Corpus loader for the bundled Letter Flick puzzle JSON.
//
// `letter-flick-daily-puzzles-v1.0.json` is the canonical web-side corpus,
// produced by `scripts/letter_flick/build_daily_puzzles_v1_0.py`. Each puzzle
// carries its own `letter_flick_puzzle_date` and `letter_flick_puzzle_number`
// — the web app does date-keyed lookup, not modulo-on-index. Vite inlines
// the JSON into the bundle at build time thanks to tsconfig's
// `resolveJsonModule`. The shape is fixed by the build script's curator
// output; we mirror that shape here.
//
// `as CorpusHeader` is the standard Vite typed-JSON-import pattern. It is
// NOT a `as any` / `as unknown as` escape hatch — the runtime shape is
// produced by a script that lives in the same repo and is asserted on every
// CI build via the script's own validation. If the script changes, both files
// move together.

import corpusJson from '../data/letter-flick-daily-puzzles-v1.0.json'
import type { Puzzle } from './puzzle'
import {
  parseWireSolutionGraph,
  type WireSolutionGraph,
} from './solutionGraph'

// The on-wire solution-DAG shape (spec §10) is shared with the practice corpus
// and lives in solutionGraph.ts as `WireSolutionGraph`; `parseWireSolutionGraph`
// validates + encodes it. Optional + additive on CorpusPuzzle below: absent on
// every daily puzzle today, arrives when a future pack carries `solution_graph`.

export interface CorpusPuzzle {
  readonly id: string
  // Curator-assigned local-date string ('YYYY-MM-DD') and human display
  // number. Pulled from the JSON unchanged — the daily selector matches
  // today's formatLocalDate against `letter_flick_puzzle_date`, and the
  // share-grid header reads `letter_flick_puzzle_number`. Both fields are
  // snake_case to match the curator-side build script; engine wire fields
  // below stay camelCase.
  readonly letter_flick_puzzle_date: string
  readonly letter_flick_puzzle_number: number
  readonly targetWord: string
  readonly wordLength: number
  readonly initialLayout: readonly number[]
  readonly letterByTokenIdx: readonly string[]
  // The per-puzzle solution DAG, parsed + validated by parseWireSolutionGraph.
  // The deployed JSON still also carries a legacy `validChoicesPerSlot` key —
  // it is intentionally NOT declared here and NOT read by any code; the
  // `as unknown as CorpusHeader` cast tolerates the extra wire key.
  readonly solution_graph?: WireSolutionGraph
}

export interface CorpusHeader {
  readonly letter_flick_daily_puzzles_version: string
  readonly puzzle_pack_version: string
  readonly curated_on: string
  readonly launch_pool_first_date: string
  readonly launch_pool_last_date: string
  readonly launch_pool_size: number
  readonly launch_pool_ids: readonly string[]
  readonly puzzle_count: number
  readonly puzzles: readonly CorpusPuzzle[]
}

export const CORPUS: CorpusHeader = corpusJson as unknown as CorpusHeader

// Module-load runtime assertions. The corpus version is bumped manually each
// time the source schema turns over; this guard makes the failure mode loud +
// obvious if a rebuild gets stale, rather than silently rendering every
// correct slot orange.
const EXPECTED_VERSION = 'letter-flick-daily-puzzles-v1.0' as const
if (CORPUS.letter_flick_daily_puzzles_version !== EXPECTED_VERSION) {
  throw new Error(
    `Corpus version mismatch: expected '${EXPECTED_VERSION}', got ` +
      `'${CORPUS.letter_flick_daily_puzzles_version}'. Rebuild ` +
      `apps/web/src/data/letter-flick-daily-puzzles-v1.0.json with ` +
      `scripts/letter_flick/build_daily_puzzles_v1_0.py.`,
  )
}
// Sort-order guard: the daily selector's clamp + closest-date fallback
// assumes puzzles are sorted ascending by date. ISO 'YYYY-MM-DD' is
// lex-sortable, so direct string compare suffices — no Date parse needed.
// Loud failure if the curator script ever produces an out-of-order corpus.
for (let i = 1; i < CORPUS.puzzles.length; i += 1) {
  const prev = CORPUS.puzzles[i - 1]
  const curr = CORPUS.puzzles[i]
  if (prev === undefined || curr === undefined) continue
  if (curr.letter_flick_puzzle_date <= prev.letter_flick_puzzle_date) {
    throw new Error(
      `Corpus malformed: puzzles must be sorted ascending by ` +
        `letter_flick_puzzle_date. Index ${String(i)} ` +
        `(${curr.letter_flick_puzzle_date}) <= index ${String(i - 1)} ` +
        `(${prev.letter_flick_puzzle_date}).`,
    )
  }
}

// Adapter: corpus entries are already shape-compatible with `Puzzle`, but
// going through this function gives us a single place to evolve the corpus
// schema without breaking the Puzzle consumer contract. The runtime Puzzle
// interface does NOT carry date/number — those stay on CorpusPuzzle for
// daily lookups; the playable shape only needs the engine fields.
export function corpusEntryToPuzzle(entry: CorpusPuzzle): Puzzle {
  // Parse the optional DAG via the shared validator. undefined (no field, or
  // malformed) → the runtime Puzzle simply omits solutionGraph and every
  // consumer degrades to the union path. With exactOptionalPropertyTypes we omit
  // the key entirely rather than set it to undefined.
  const solutionGraph = parseWireSolutionGraph(entry.solution_graph)
  const base = {
    id: entry.id,
    targetWord: entry.targetWord,
    wordLength: entry.wordLength,
    initialLayout: entry.initialLayout,
    letterByTokenIdx: entry.letterByTokenIdx,
  }
  if (solutionGraph === undefined) return base
  return { ...base, solutionGraph }
}
